// Feb-2026 · Otras Obligaciones (Disp. 3ª / 43ª / seguimiento 52ª A-XI).
// Alimenta las secciones 14.1, 14.2, 14.3 y 15.1 del Informe de Auditoría PLD/FT:
//   · 14.1 Aviso de personas que ejercen el Control de la Entidad.
//   · 14.2 Aviso sobre transmisión de acciones.
//   · 14.3 Atención de requerimientos de autoridad.
//   · 15.1 Seguimiento de hallazgos del ejercicio auditado anterior.
import { useEffect, useState, useCallback } from "react";
import {
  ClipboardCheck, FileText, Users, Gavel, Loader2, Plus, Trash2, Pencil,
  RefreshCw, ShieldAlert,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";


const OFICIO_ESTATUS = ["Recibido", "En atención", "Respondido", "Cerrado"];
const AVISO_TIPOS = [
  { value: "control_entidad", label: "Personas que ejercen el Control (14.1)" },
  { value: "transmision_acciones", label: "Transmisión de acciones (14.2)" },
];
const HALLAZGO_ESTATUS = ["Abierto", "En proceso", "Cerrado"];
const HALLAZGO_CALIFICACIONES = [
  "Cumple", "Cumple mayoritariamente", "Cumple parcialmente",
  "No cumple", "No aplica",
];

const COLOR_OFICIO = {
  "Recibido":    "bg-amber-100 text-amber-800 border-amber-300",
  "En atención": "bg-blue-100 text-blue-800 border-blue-300",
  "Respondido":  "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Cerrado":     "bg-slate-100 text-slate-700 border-slate-300",
};
const COLOR_HALLAZGO = {
  "Abierto":     "bg-red-100 text-red-800 border-red-300",
  "En proceso":  "bg-amber-100 text-amber-800 border-amber-300",
  "Cerrado":     "bg-emerald-100 text-emerald-800 border-emerald-300",
};


function EmptyState({ icon: Icon, titulo, sub }) {
  return (
    <div className="text-center py-10 text-slate-500" data-testid="empty-state">
      <Icon className="h-8 w-8 mx-auto mb-2 opacity-60" />
      <div className="text-sm font-medium">{titulo}</div>
      {sub && <div className="text-xs mt-1">{sub}</div>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// OFICIOS (14.3)
// ═══════════════════════════════════════════════════════════════════════════
function OficiosPanel({ puedeEscribir, refresh, refreshResumen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/otras-obligaciones/oficios");
      setRows(data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refresh]);

  const abrirNuevo = () => {
    setEdit(null);
    setForm({ estatus: "Recibido" });
    setShow(true);
  };
  const abrirEditar = (r) => {
    setEdit(r); setForm({ ...r }); setShow(true);
  };
  const guardar = async () => {
    try {
      if (edit) {
        await api.put(`/otras-obligaciones/oficios/${edit.id}`, form);
        toast.success("Oficio actualizado.");
      } else {
        await api.post("/otras-obligaciones/oficios", form);
        toast.success("Oficio registrado.");
      }
      setShow(false); await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const borrar = async (r) => {
    if (!window.confirm(`¿Eliminar oficio ${r.folio_oficio}?`)) return;
    try {
      await api.delete(`/otras-obligaciones/oficios/${r.id}`);
      toast.success("Oficio eliminado.");
      await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="panel-oficios">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">
          <Gavel className="h-4 w-4 inline mr-1" />
          Oficios y requerimientos de autoridad (Disp. 43ª) · alimenta el punto 14.3 del Informe de Auditoría.
        </div>
        {puedeEscribir && (
          <Button size="sm" onClick={abrirNuevo} data-testid="btn-nuevo-oficio">
            <Plus className="h-4 w-4 mr-1" /> Nuevo oficio
          </Button>
        )}
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-6" />
        : rows.length === 0 ? <EmptyState icon={Gavel} titulo="Sin oficios registrados" sub="Registra requerimientos recibidos de la CNBV/UIF u otra autoridad." />
        : (
        <div className="overflow-x-auto border rounded-lg" data-testid="tbl-oficios">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Folio</th>
                <th className="text-left px-3 py-2">Autoridad</th>
                <th className="text-left px-3 py-2">Materia</th>
                <th className="text-left px-3 py-2">Recibido</th>
                <th className="text-left px-3 py-2">Vence</th>
                <th className="text-left px-3 py-2">Estatus</th>
                <th className="text-left px-3 py-2">Respuesta</th>
                {puedeEscribir && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.folio_oficio}</td>
                  <td className="px-3 py-2">{r.autoridad_emisora}</td>
                  <td className="px-3 py-2 max-w-xs truncate">{r.materia || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.fecha_recibido}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.fecha_vencimiento || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge className={`border ${COLOR_OFICIO[r.estatus] || ""}`}>{r.estatus}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.folio_respuesta ? `${r.folio_respuesta} (${r.fecha_respuesta || "—"})` : "—"}
                  </td>
                  {puedeEscribir && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => abrirEditar(r)} data-testid={`btn-editar-oficio-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => borrar(r)} data-testid={`btn-borrar-oficio-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-2xl" data-testid="dlg-oficio">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar oficio" : "Nuevo oficio / requerimiento"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Folio del oficio *</Label>
              <Input value={form.folio_oficio || ""} onChange={e => setForm({ ...form, folio_oficio: e.target.value })} data-testid="in-folio-oficio" />
            </div>
            <div>
              <Label>Autoridad emisora *</Label>
              <Input value={form.autoridad_emisora || ""} onChange={e => setForm({ ...form, autoridad_emisora: e.target.value })} data-testid="in-autoridad" placeholder="CNBV Vice PLD, UIF, PGR..." />
            </div>
            <div className="col-span-2">
              <Label>Materia</Label>
              <Input value={form.materia || ""} onChange={e => setForm({ ...form, materia: e.target.value })} data-testid="in-materia" />
            </div>
            <div className="col-span-2">
              <Label>Resumen</Label>
              <Textarea rows={3} value={form.resumen || ""} onChange={e => setForm({ ...form, resumen: e.target.value })} data-testid="in-resumen" />
            </div>
            <div>
              <Label>Fecha recibido *</Label>
              <Input type="date" value={form.fecha_recibido || ""} onChange={e => setForm({ ...form, fecha_recibido: e.target.value })} data-testid="in-fecha-recibido" />
            </div>
            <div>
              <Label>Fecha vencimiento</Label>
              <Input type="date" value={form.fecha_vencimiento || ""} onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })} data-testid="in-fecha-vence" />
            </div>
            <div>
              <Label>Estatus *</Label>
              <Select value={form.estatus || "Recibido"} onValueChange={v => setForm({ ...form, estatus: v })}>
                <SelectTrigger data-testid="sel-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OFICIO_ESTATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha respuesta</Label>
              <Input type="date" value={form.fecha_respuesta || ""} onChange={e => setForm({ ...form, fecha_respuesta: e.target.value })} data-testid="in-fecha-respuesta" />
            </div>
            <div className="col-span-2">
              <Label>Folio respuesta</Label>
              <Input value={form.folio_respuesta || ""} onChange={e => setForm({ ...form, folio_respuesta: e.target.value })} data-testid="in-folio-respuesta" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-guardar-oficio">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// AVISOS DE CONTROL (14.1 / 14.2)
// ═══════════════════════════════════════════════════════════════════════════
function AvisosPanel({ puedeEscribir, refresh, refreshResumen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ tipo_aviso: "control_entidad", personas: [{}] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/otras-obligaciones/avisos-control");
      setRows(data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refresh]);

  const abrirNuevo = () => {
    setEdit(null);
    setForm({ tipo_aviso: "control_entidad", medio_presentacion: "SITI", personas: [{}] });
    setShow(true);
  };
  const abrirEditar = (r) => {
    setEdit(r);
    setForm({ ...r, personas: (r.personas && r.personas.length ? r.personas : [{}]) });
    setShow(true);
  };
  const guardar = async () => {
    try {
      const payload = { ...form, personas: (form.personas || []).filter(p => (p.nombre || "").trim()) };
      if (edit) {
        await api.put(`/otras-obligaciones/avisos-control/${edit.id}`, payload);
        toast.success("Aviso actualizado.");
      } else {
        await api.post("/otras-obligaciones/avisos-control", payload);
        toast.success("Aviso registrado.");
      }
      setShow(false); await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const borrar = async (r) => {
    if (!window.confirm("¿Eliminar aviso?")) return;
    try {
      await api.delete(`/otras-obligaciones/avisos-control/${r.id}`);
      toast.success("Aviso eliminado.");
      await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setPersona = (idx, key, value) => {
    const p = [...(form.personas || [])];
    p[idx] = { ...(p[idx] || {}), [key]: value };
    setForm({ ...form, personas: p });
  };
  const addPersona = () => setForm({ ...form, personas: [...(form.personas || []), {}] });
  const rmPersona = (idx) => {
    const p = (form.personas || []).filter((_, i) => i !== idx);
    setForm({ ...form, personas: p.length ? p : [{}] });
  };

  return (
    <div className="space-y-3" data-testid="panel-avisos">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">
          <Users className="h-4 w-4 inline mr-1" />
          Avisos de control y transmisión de acciones (Disp. 3ª) · alimenta 14.1 y 14.2.
        </div>
        {puedeEscribir && (
          <Button size="sm" onClick={abrirNuevo} data-testid="btn-nuevo-aviso">
            <Plus className="h-4 w-4 mr-1" /> Nuevo aviso
          </Button>
        )}
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-6" />
        : rows.length === 0 ? <EmptyState icon={Users} titulo="Sin avisos registrados" sub="Captura avisos de control o transmisión de acciones enviados a CNBV." />
        : (
        <div className="overflow-x-auto border rounded-lg" data-testid="tbl-avisos">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Folio</th>
                <th className="text-left px-3 py-2">Medio</th>
                <th className="text-left px-3 py-2">Personas</th>
                {puedeEscribir && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Badge variant="outline">
                      {r.tipo_aviso === "control_entidad" ? "Control (14.1)" : "Transmisión (14.2)"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.fecha_aviso}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.oficio_folio || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.medio_presentacion}</td>
                  <td className="px-3 py-2 text-xs">
                    {(r.personas || []).map(p => p.nombre + (p.porcentaje ? ` (${p.porcentaje})` : "")).join(", ") || "—"}
                  </td>
                  {puedeEscribir && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => abrirEditar(r)} data-testid={`btn-editar-aviso-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => borrar(r)} data-testid={`btn-borrar-aviso-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-3xl" data-testid="dlg-aviso">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar aviso" : "Nuevo aviso"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de aviso *</Label>
              <Select value={form.tipo_aviso || "control_entidad"} onValueChange={v => setForm({ ...form, tipo_aviso: v })}>
                <SelectTrigger data-testid="sel-tipo-aviso"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AVISO_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha del aviso *</Label>
              <Input type="date" value={form.fecha_aviso || ""} onChange={e => setForm({ ...form, fecha_aviso: e.target.value })} data-testid="in-fecha-aviso" />
            </div>
            <div>
              <Label>Folio del oficio</Label>
              <Input value={form.oficio_folio || ""} onChange={e => setForm({ ...form, oficio_folio: e.target.value })} data-testid="in-folio-aviso" />
            </div>
            <div>
              <Label>Medio de presentación</Label>
              <Input value={form.medio_presentacion || "SITI"} onChange={e => setForm({ ...form, medio_presentacion: e.target.value })} placeholder="SITI / Oficio impreso / Correo" data-testid="in-medio" />
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas || ""} onChange={e => setForm({ ...form, notas: e.target.value })} data-testid="in-notas-aviso" />
            </div>
          </div>
          <div className="mt-2">
            <div className="flex justify-between items-center mb-2">
              <Label className="text-sm font-medium">Personas involucradas *</Label>
              <Button size="sm" variant="outline" onClick={addPersona} data-testid="btn-add-persona">
                <Plus className="h-3 w-3 mr-1" /> Añadir persona
              </Button>
            </div>
            <div className="space-y-2">
              {(form.personas || []).map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2">
                  <div className="col-span-4">
                    <Label className="text-xs">Nombre</Label>
                    <Input value={p.nombre || ""} onChange={e => setPersona(i, "nombre", e.target.value)} data-testid={`in-persona-nombre-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">RFC</Label>
                    <Input value={p.rfc || ""} onChange={e => setPersona(i, "rfc", e.target.value)} maxLength={13} data-testid={`in-persona-rfc-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">CURP</Label>
                    <Input value={p.curp || ""} onChange={e => setPersona(i, "curp", e.target.value)} maxLength={18} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">%</Label>
                    <Input value={p.porcentaje || ""} onChange={e => setPersona(i, "porcentaje", e.target.value)} data-testid={`in-persona-pct-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Cargo</Label>
                    <Input value={p.cargo || ""} onChange={e => setPersona(i, "cargo", e.target.value)} data-testid={`in-persona-cargo-${i}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => rmPersona(i)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-guardar-aviso">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// HALLAZGOS (15.1)
// ═══════════════════════════════════════════════════════════════════════════
function HallazgosPanel({ puedeEscribir, refresh, refreshResumen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [filtroEjercicio, setFiltroEjercicio] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filtroEjercicio ? `/otras-obligaciones/hallazgos?ejercicio=${filtroEjercicio}` : "/otras-obligaciones/hallazgos";
      const { data } = await api.get(url);
      setRows(data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [filtroEjercicio]);
  useEffect(() => { load(); }, [load, refresh]);

  const abrirNuevo = () => {
    setEdit(null);
    const anioAnterior = new Date().getFullYear() - 1;
    setForm({ ejercicio: anioAnterior, estatus_seguimiento: "Abierto" });
    setShow(true);
  };
  const abrirEditar = (r) => { setEdit(r); setForm({ ...r }); setShow(true); };
  const guardar = async () => {
    try {
      if (edit) {
        await api.put(`/otras-obligaciones/hallazgos/${edit.id}`, form);
        toast.success("Hallazgo actualizado.");
      } else {
        await api.post("/otras-obligaciones/hallazgos", form);
        toast.success("Hallazgo registrado.");
      }
      setShow(false); await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const borrar = async (r) => {
    if (!window.confirm(`¿Eliminar hallazgo del punto ${r.punto}?`)) return;
    try {
      await api.delete(`/otras-obligaciones/hallazgos/${r.id}`);
      toast.success("Hallazgo eliminado.");
      await load(); refreshResumen?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="panel-hallazgos">
      <div className="flex justify-between items-center gap-3">
        <div className="text-sm text-slate-600 flex-1">
          <FileText className="h-4 w-4 inline mr-1" />
          Seguimiento de hallazgos del Informe de Auditoría anterior (52ª A-XI) · alimenta 15.1.
        </div>
        <Input
          type="number" min="2020" max="2099" className="w-24"
          placeholder="Ejercicio" value={filtroEjercicio}
          onChange={e => setFiltroEjercicio(e.target.value)}
          data-testid="in-filtro-ejercicio"
        />
        {puedeEscribir && (
          <Button size="sm" onClick={abrirNuevo} data-testid="btn-nuevo-hallazgo">
            <Plus className="h-4 w-4 mr-1" /> Nuevo hallazgo
          </Button>
        )}
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-6" />
        : rows.length === 0 ? <EmptyState icon={FileText} titulo="Sin hallazgos registrados" sub="Captura los hallazgos del informe anterior y su plan de acción." />
        : (
        <div className="overflow-x-auto border rounded-lg" data-testid="tbl-hallazgos">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Ejercicio</th>
                <th className="text-left px-3 py-2">Punto</th>
                <th className="text-left px-3 py-2">Descripción</th>
                <th className="text-left px-3 py-2">Responsable</th>
                <th className="text-left px-3 py-2">Compromiso</th>
                <th className="text-left px-3 py-2">Estatus</th>
                {puedeEscribir && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs">{r.ejercicio}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.punto}</td>
                  <td className="px-3 py-2 max-w-md">
                    <div className="text-xs line-clamp-2">{r.descripcion_hallazgo}</div>
                    {r.calificacion_original && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Calif. original: {r.calificacion_original}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.responsable || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.fecha_compromiso || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge className={`border ${COLOR_HALLAZGO[r.estatus_seguimiento] || ""}`}>
                      {r.estatus_seguimiento}
                    </Badge>
                    {r.fecha_cierre && (
                      <div className="text-[10px] text-slate-500 mt-0.5">Cerrado {r.fecha_cierre}</div>
                    )}
                  </td>
                  {puedeEscribir && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => abrirEditar(r)} data-testid={`btn-editar-hallazgo-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => borrar(r)} data-testid={`btn-borrar-hallazgo-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-2xl" data-testid="dlg-hallazgo">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar hallazgo" : "Nuevo hallazgo"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ejercicio auditado *</Label>
              <Input type="number" value={form.ejercicio || ""} onChange={e => setForm({ ...form, ejercicio: e.target.value })} data-testid="in-hallazgo-ejercicio" />
            </div>
            <div>
              <Label>Punto (ej. 3.4) *</Label>
              <Input value={form.punto || ""} onChange={e => setForm({ ...form, punto: e.target.value })} data-testid="in-hallazgo-punto" placeholder="3.4" />
            </div>
            <div className="col-span-2">
              <Label>Descripción del hallazgo *</Label>
              <Textarea rows={3} value={form.descripcion_hallazgo || ""} onChange={e => setForm({ ...form, descripcion_hallazgo: e.target.value })} data-testid="in-hallazgo-desc" />
            </div>
            <div>
              <Label>Calificación original</Label>
              <Select value={form.calificacion_original || ""} onValueChange={v => setForm({ ...form, calificacion_original: v })}>
                <SelectTrigger data-testid="sel-hallazgo-calif"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {HALLAZGO_CALIFICACIONES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estatus de seguimiento *</Label>
              <Select value={form.estatus_seguimiento || "Abierto"} onValueChange={v => setForm({ ...form, estatus_seguimiento: v })}>
                <SelectTrigger data-testid="sel-hallazgo-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HALLAZGO_ESTATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Plan de acción</Label>
              <Textarea rows={2} value={form.plan_accion || ""} onChange={e => setForm({ ...form, plan_accion: e.target.value })} data-testid="in-hallazgo-plan" />
            </div>
            <div>
              <Label>Responsable</Label>
              <Input value={form.responsable || ""} onChange={e => setForm({ ...form, responsable: e.target.value })} data-testid="in-hallazgo-resp" />
            </div>
            <div>
              <Label>Fecha compromiso</Label>
              <Input type="date" value={form.fecha_compromiso || ""} onChange={e => setForm({ ...form, fecha_compromiso: e.target.value })} data-testid="in-hallazgo-fecha-comp" />
            </div>
            <div>
              <Label>Fecha cierre</Label>
              <Input type="date" value={form.fecha_cierre || ""} onChange={e => setForm({ ...form, fecha_cierre: e.target.value })} data-testid="in-hallazgo-fecha-cierre" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-guardar-hallazgo">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
export default function OtrasObligaciones() {
  const { user } = useAuth();
  const puedeEscribir = ["admin_entidad", "oficial_cumplimiento"].includes(user?.role);
  const puedeVer = [
    "admin_entidad", "oficial_cumplimiento", "tecnico", "super_admin", "auditor_externo",
  ].includes(user?.role);

  const [tab, setTab] = useState("oficios");
  const [resumen, setResumen] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadResumen = useCallback(async () => {
    try {
      const { data } = await api.get("/otras-obligaciones/resumen");
      setResumen(data);
    } catch (e) { /* silencioso */ }
  }, []);
  useEffect(() => { if (puedeVer) loadResumen(); }, [loadResumen, puedeVer, refreshTick]);

  if (!puedeVer) {
    return (
      <div className="p-6" data-testid="page-otras-obligaciones-no-acceso">
        <Alert className="border-red-200 bg-red-50">
          <ShieldAlert className="h-4 w-4 text-red-600" />
          <AlertDescription>Tu perfil no tiene acceso a Otras Obligaciones.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const refreshAll = () => setRefreshTick(t => t + 1);

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="page-otras-obligaciones">
      <PageHeader
        icon={<ClipboardCheck className="h-5 w-5" />}
        title="Otras Obligaciones"
        subtitle="Oficios, avisos de control y hallazgos · alimenta secciones 14 y 15 del Informe de Auditoría PLD/FT (Disp. 3ª, 43ª, 52ª A-XI)."
      />

      {resumen && (
        <div className="grid grid-cols-3 gap-3" data-testid="resumen-cards">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Oficios / requerimientos</div>
                  <div className="text-2xl font-semibold" data-testid="stat-oficios">{resumen.oficios_cnbv.total}</div>
                </div>
                <Gavel className="h-8 w-8 text-slate-300" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {Object.entries(resumen.oficios_cnbv.por_estatus || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Avisos de control</div>
                  <div className="text-2xl font-semibold" data-testid="stat-avisos">{resumen.avisos_control_entidad.total}</div>
                </div>
                <Users className="h-8 w-8 text-slate-300" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {Object.entries(resumen.avisos_control_entidad.por_tipo || {}).map(([k, v]) => `${k === "control_entidad" ? "14.1" : "14.2"}: ${v}`).join(" · ") || "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Hallazgos en seguimiento</div>
                  <div className="text-2xl font-semibold" data-testid="stat-hallazgos">{resumen.hallazgos_auditoria.total}</div>
                </div>
                <FileText className="h-8 w-8 text-slate-300" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {Object.entries(resumen.hallazgos_auditoria.por_estatus || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex justify-between items-center">
          <TabsList data-testid="tabs-otras-obligaciones">
            <TabsTrigger value="oficios" data-testid="tab-oficios">
              <Gavel className="h-4 w-4 mr-1" /> Oficios (14.3)
            </TabsTrigger>
            <TabsTrigger value="avisos" data-testid="tab-avisos">
              <Users className="h-4 w-4 mr-1" /> Avisos control (14.1/14.2)
            </TabsTrigger>
            <TabsTrigger value="hallazgos" data-testid="tab-hallazgos">
              <FileText className="h-4 w-4 mr-1" /> Hallazgos (15.1)
            </TabsTrigger>
          </TabsList>
          <Button size="sm" variant="outline" onClick={refreshAll} data-testid="btn-refresh-otras">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
          </Button>
        </div>
        <TabsContent value="oficios" className="mt-4">
          <OficiosPanel puedeEscribir={puedeEscribir} refresh={refreshTick} refreshResumen={loadResumen} />
        </TabsContent>
        <TabsContent value="avisos" className="mt-4">
          <AvisosPanel puedeEscribir={puedeEscribir} refresh={refreshTick} refreshResumen={loadResumen} />
        </TabsContent>
        <TabsContent value="hallazgos" className="mt-4">
          <HallazgosPanel puedeEscribir={puedeEscribir} refresh={refreshTick} refreshResumen={loadResumen} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
